import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import { Link } from 'react-router-dom';
import { Camera, Video, Plane, ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';

export default function About() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/public/settings')
      .then(res => setSettings(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Filtra le statistiche visibili e ordinate
  const visibleStats = (settings?.stats || [])
    .filter(s => s.is_visible)
    .sort((a, b) => a.order - b.order);

  return (
    <div className="pt-32 pb-24" data-testid="about-page">
      {/* Header */}
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <span className="text-sm tracking-[0.2em] uppercase text-muted-foreground">Chi Sono</span>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl mt-4">
            {settings?.about_title || "Andrea Caruso"}
          </h1>
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="aspect-[4/5] overflow-hidden bg-muted">
              <img
                src={settings?.about_image || "https://images.unsplash.com/photo-1639815675801-44eb29a33901?w=800"}
                alt="Andrea Caruso"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <p className="font-accent text-2xl lg:text-3xl text-muted-foreground mb-8 leading-relaxed">
              {settings?.hero_tagline || "Catturare l'essenza, raccontare l'emozione."}
            </p>
            
            {/* About Text - supports HTML from Rich Text Editor */}
            <div 
              className="prose prose-lg text-muted-foreground mb-8"
              dangerouslySetInnerHTML={{ 
                __html: DOMPurify.sanitize(settings?.about_text || `<p>Sono Andrea Caruso, fotografo e videomaker con oltre 10 anni di esperienza. 
                La mia passione è catturare momenti autentici e trasformarli in ricordi indimenticabili. 
                Specializzato in matrimoni, ritratti ed eventi, utilizzo anche tecnologia drone per riprese aeree mozzafiato.</p>`)
              }}
            />

            {/* Skills */}
            <div className="grid grid-cols-3 gap-6 mb-8">
              <div className="text-center p-4 bg-muted/50">
                <Camera className="mx-auto mb-2" size={28} />
                <span className="text-sm">Fotografia</span>
              </div>
              <div className="text-center p-4 bg-muted/50">
                <Video className="mx-auto mb-2" size={28} />
                <span className="text-sm">Video</span>
              </div>
              <div className="text-center p-4 bg-muted/50">
                <Plane className="mx-auto mb-2" size={28} />
                <span className="text-sm">Drone</span>
              </div>
            </div>

            <Link to="/prenota">
              <Button size="lg" data-testid="about-cta-btn">
                Lavoriamo Insieme
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>

        {/* Stats - Now Dynamic from CMS */}
        {visibleStats.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`grid grid-cols-2 md:grid-cols-${Math.min(visibleStats.length, 4)} gap-8 py-16 border-y border-border`}
          >
            {visibleStats.map((stat) => (
              <div key={stat.id} className="text-center">
                <div className="font-heading text-5xl lg:text-6xl mb-2">
                  {stat.value}<span className="text-primary">{stat.suffix}</span>
                </div>
                <div className="text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
